import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { FeedbackType, NotifyPayload, registerFeedbackHandler } from '@/lib/feedback';

export interface RecentActivity {
  id: string;
  title: string;
  description?: string;
  type: FeedbackType;
  createdAt: string;
}

interface ToastItem {
  id: string;
  title: string;
  description?: string;
  type: FeedbackType;
}

interface ActionFeedbackContextValue {
  activities: RecentActivity[];
  notify: (payload: NotifyPayload) => void;
  clearActivities: () => void;
}

const ActionFeedbackContext = createContext<ActionFeedbackContextValue | null>(null);

const STORAGE_KEY = 'smala_recent_activities';
const MAX_ACTIVITIES = 50;

const readInitialActivities = (): RecentActivity[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter((item) => item && typeof item.id === 'string' && typeof item.title === 'string');
  } catch {
    return [];
  }
};

export function ActionFeedbackProvider({ children }: { children: React.ReactNode }) {
  const [activities, setActivities] = useState<RecentActivity[]>(() => readInitialActivities());
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const clearActivities = useCallback(() => {
    setActivities([]);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const notify = useCallback((payload: NotifyPayload) => {
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const type = payload.type || 'success';

    setToasts((prev) => [
      {
        id,
        title: payload.title,
        description: payload.description,
        type,
      },
      ...prev,
    ]);

    window.setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 3500);

    if (payload.addToActivity === false) {
      return;
    }

    setActivities((prev) => {
      const next: RecentActivity[] = [
        {
          id,
          title: payload.title,
          description: payload.description,
          type,
          createdAt: new Date().toISOString(),
        },
        ...prev,
      ].slice(0, MAX_ACTIVITIES);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }

      return next;
    });
  }, []);

  const value = useMemo<ActionFeedbackContextValue>(
    () => ({
      activities,
      notify,
      clearActivities,
    }),
    [activities, notify, clearActivities]
  );

  useEffect(() => {
    registerFeedbackHandler(notify);

    return () => {
      registerFeedbackHandler(null);
    };
  }, [notify]);

  return (
    <ActionFeedbackContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className="rounded-lg border bg-white p-3 shadow-lg"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-start gap-2">
              {toast.type === 'success' && <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />}
              {toast.type === 'error' && <AlertCircle className="mt-0.5 h-4 w-4 text-red-600" />}
              {toast.type === 'info' && <Info className="mt-0.5 h-4 w-4 text-blue-600" />}
              <div className="flex-1">
                <p className="text-sm font-semibold">{toast.title}</p>
                {toast.description ? (
                  <p className="text-xs text-muted-foreground">{toast.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => setToasts((prev) => prev.filter((item) => item.id !== toast.id))}
                className="rounded p-1 text-muted-foreground hover:bg-gray-100"
                aria-label="Tutup"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ActionFeedbackContext.Provider>
  );
}

export function useActionFeedbackContext() {
  const context = useContext(ActionFeedbackContext);

  if (!context) {
    throw new Error('useActionFeedbackContext must be used within ActionFeedbackProvider');
  }

  return context;
}
